import { useEffect, useState } from "react";
import { Loader2, Pipette, RotateCcw } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Input } from "@/shared/ui/input";
import { errorText } from "@/shared/lib/apiErrors";
import { cn } from "@/shared/lib/utils";

import type { HostPalette, HostSwatch, SiteTheme } from "../../../api/types";
import { normalizeHex } from "../../../model/hostExtras";

function Swatches({ label, items }: { label: string; items: HostSwatch[] }) {
  if (items.length === 0) return null;
  return (
    <div className="flex items-center gap-2 text-2xs text-muted-foreground">
      <span className="w-14 shrink-0">{label}</span>
      <ul aria-label={label} className="flex flex-wrap gap-1">
        {items.slice(0, 6).map((s) => (
          <li
            key={s.color}
            title={`${s.color} · ${s.count} 处`}
            className="h-4 w-4 rounded border border-border"
            style={{ backgroundColor: s.color }}
          />
        ))}
      </ul>
    </div>
  );
}

/** 一个颜色输入:色块选择 + 十六进制文本框(失焦 / 回车才提交,输到一半不乱写)。 */
function ColorInput({
  label,
  value,
  fallback,
  disabled,
  onCommit,
}: {
  label: string;
  value: string | undefined;
  fallback: string;
  disabled?: boolean;
  onCommit: (hex: string) => void;
}) {
  const [text, setText] = useState(value ?? "");
  useEffect(() => setText(value ?? ""), [value]);
  const commit = () => {
    const hex = normalizeHex(text);
    if (hex) onCommit(hex);
    else setText(value ?? "");
  };
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="w-14 shrink-0 text-muted-foreground">{label}</span>
      <input
        type="color"
        aria-label={`${label}色块`}
        className="h-7 w-9 cursor-pointer rounded border border-border bg-transparent p-0.5"
        value={(value ?? fallback).toLowerCase()}
        disabled={disabled}
        onChange={(e) => onCommit(normalizeHex(e.target.value) ?? e.target.value)}
      />
      <Input
        aria-label={label}
        className="h-7 w-28 font-mono text-xs"
        placeholder={fallback}
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
    </label>
  );
}

/**
 * 1009 编辑页「品牌配色」:模板色板(主色 / 强调色 / 背景 / 文字)+ 改主色(可选强调色)+ 从 logo 取色 + 恢复模板配色。
 * ★ 只换主色组(和设了强调色时的强调色组),背景 / 文字 / 中性色不动;不设 = 一个颜色都不发(沿用模板配色)。
 */
export function HostThemePanel({
  palette,
  theme,
  hasLogo,
  disabled,
  onChange,
  pickLogoColor,
}: {
  palette: HostPalette | null;
  theme: SiteTheme | undefined;
  hasLogo: boolean;
  disabled?: boolean;
  onChange: (theme: SiteTheme | null) => void;
  /** 取 logo 主色;取不出(黑白 logo)回 null。 */
  pickLogoColor: () => Promise<string | null>;
}) {
  const [picking, setPicking] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  if (!palette) return null;
  const primaryGroup = palette.primary;

  const fromLogo = async () => {
    setPicking(true);
    setNote(null);
    try {
      const c = await pickLogoColor();
      if (c) onChange({ ...(theme ?? {}), primary: c });
      else setNote("Logo 里没有明显的彩色(黑白 / 灰的 logo 取不出主色),请手动选");
    } catch (e) {
      setNote(`取色失败:${errorText(e)}`);
    } finally {
      setPicking(false);
    }
  };

  return (
    <section
      aria-label="品牌配色"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">品牌配色</h2>
        <p className="mt-0.5 text-2xs text-muted-foreground">
          {primaryGroup
            ? `模板主色 ${primaryGroup.color} 共 ${primaryGroup.count} 处;改成你的主色后这些位置一起换(深浅自动派生),背景和文字颜色不变。`
            : "这套模板只有黑白灰,没有可换的主色。"}
        </p>
      </div>
      <div className="space-y-1.5">
        {primaryGroup && <Swatches label="模板主色" items={primaryGroup.members} />}
        {palette.accent && <Swatches label="模板强调色" items={palette.accent.members} />}
        <Swatches label="背景" items={palette.background} />
        <Swatches label="文字" items={palette.text} />
      </div>
      {primaryGroup && (
        <div className="space-y-2">
          <ColorInput
            label="主色"
            value={theme?.primary}
            fallback={primaryGroup.color}
            disabled={disabled}
            onCommit={(hex) => onChange({ ...(theme ?? {}), primary: hex })}
          />
          {palette.accent && (
            <ColorInput
              label="强调色"
              value={theme?.accent}
              fallback={palette.accent.color}
              disabled={disabled || !theme?.primary}
              onCommit={(hex) => (theme?.primary ? onChange({ ...theme, accent: hex }) : undefined)}
            />
          )}
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              disabled={disabled || !hasLogo || picking}
              title={hasLogo ? undefined : "先上传 Logo"}
              onClick={() => void fromLogo()}
            >
              {picking ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Pipette className="h-3 w-3" />
              )}
              从 Logo 取色
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={disabled || !theme}
              onClick={() => onChange(null)}
            >
              <RotateCcw className="h-3 w-3" />
              恢复模板配色
            </Button>
          </div>
          <p className="text-2xs text-muted-foreground" role="status">
            {theme?.primary
              ? `发布后主色换成 ${theme.primary}${theme.accent ? `,强调色换成 ${theme.accent}` : ""}`
              : "当前沿用模板配色"}
          </p>
          {note && (
            <p className="text-2xs text-warning" role="alert">
              {note}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
